
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import {
  convertRequestErrorToMap,
  isNotNullish,
  notEmpty,
  Nullable,
  useResource,
  useToast,
} from "@tager/admin-services";
import {
  createTabErrorFinder,
  FormFooter,
  OptionType,
  SeoChangeEvent,
  SeoFieldGroup,
  TabType,
  TagerFormSubmitEvent,
  FormFieldComboBox,
  FormFieldRichTextInput,
  FormFieldFileInput,
  FormField,
  FormFieldUrlAliasInput,
} from "@tager/admin-ui";
import { Page } from "@tager/admin-layout";

import { BrandType, FullCollectionType } from "@/typings/model";
import {
  createCollection,
  getBrandList,
  getCollection,
  updateCollection,
} from "@/services/requests";
import {
  convertFormValuesToCollectionCreationPayload,
  convertFormValuesToCollectionUpdatePayload,
  convertCollectionToFormValues,
  convertBrandListToOption,
  FormValues,
} from "@/views/CollectionForm/CollectionForm.helpers";
import { getCollectionFormUrl, getCollectionListUrl } from "@/utils/paths";
import { getWebsiteOrigin } from "@/utils/common";

export default defineComponent({
  name: "CollectionForm",
  components: {
    FormFooter,
    SeoFieldGroup,
    FormFieldComboBox,
    FormFieldRichTextInput,
    FormFieldFileInput,
    FormField,
    FormFieldUrlAliasInput,
    Page,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();

    const collectionId = computed<string>(
      () => route.params.collectionId as string
    );

    const isCreation = computed<boolean>(() => collectionId.value === "create");

    const [
      fetchCollection,
      { data: collection, loading: isCollectionLoading },
    ] = useResource<Nullable<FullCollectionType>>({
      fetchResource: () => getCollection(collectionId.value),
      initialValue: null,
      resourceName: "collection",
    });

    const [fetchBrandList, { data: brandList, loading: isBrandListLoading }] =
      useResource<Array<BrandType>>({
        fetchResource: getBrandList,
        initialValue: [],
        resourceName: "Brand list",
      });

    onMounted(() => {
      fetchBrandList();
      if (isCreation.value) return;

      fetchCollection();
    });

    const brandListOption = ref<Array<OptionType<number>>>(
      convertBrandListToOption(brandList.value)
    );

    const values = ref<FormValues>(
      convertCollectionToFormValues(collection.value, brandListOption.value)
    );

    const errors = ref<Record<string, string>>({});
    const isSubmitting = ref<boolean>(false);

    watch(collection, () => {
      values.value = convertCollectionToFormValues(
        collection.value,
        brandListOption.value
      );
    });

    watch(brandList, () => {
      brandListOption.value = convertBrandListToOption(brandList.value);
      values.value = convertCollectionToFormValues(
        collection.value,
        brandListOption.value
      );
    });

    function submitForm(event: TagerFormSubmitEvent) {
      isSubmitting.value = true;

      const creationBody = convertFormValuesToCollectionCreationPayload(
        values.value
      );
      const updateBody = convertFormValuesToCollectionUpdatePayload(
        values.value
      );
      const requestPromise = isCreation.value
        ? createCollection(creationBody)
        : updateCollection(collectionId.value, updateBody);

      requestPromise
        .then((response) => {
          errors.value = {};

          if (event.type === "create") {
            router.push(
              getCollectionFormUrl({ collectionId: response.data.id })
            );
          }
          if (event.type === "create_exit" || event.type === "save_exit") {
            router.push(getCollectionListUrl());
          }
          if (event.type === "create_create-another") {
            values.value = convertCollectionToFormValues(
              null,
              brandListOption.value
            );
          }

          toast.show({
            variant: "success",
            title: "Success",
            body: isCreation.value
              ? `Коллекция успешно создана`
              : "Коллекция успешно обновлена",
          });
        })
        .catch((error) => {
          console.error(error);
          errors.value = convertRequestErrorToMap(error);
          toast.show({
            variant: "danger",
            title: "Ошибка",
            body: isCreation.value
              ? `Ошибка при создании коллекции`
              : "Ошибка при обновлении коллекции",
          });
        })
        .finally(() => {
          isSubmitting.value = false;
        });
    }

    const isContentLoading = computed<boolean>(
      () => isCollectionLoading.value || isBrandListLoading.value
    );

    /** Tabs */

    const tabList = computed<Array<TabType>>(() => {
      const hasErrors = createTabErrorFinder(errors.value);

      return [
        {
          id: "common",
          label: "Основная",
          hasErrors: hasErrors([
            "name",
            "image",
            "brand",
            "content",
            "urlAlias",
          ]),
        },
        {
          id: "seo",
          label: "SEO",
          hasErrors: hasErrors([
            "pageTitle",
            "pageDescription",
            "openGraphImage",
          ]),
        },
      ].filter(notEmpty);
    });

    const selectedTabId = ref<string>(tabList.value[0].id);

    const pageTitle = computed<string>(() => {
      return isCreation.value
        ? `Добавить коллекцию`
        : `Редактировать коллекцию "${values.value.name}"`;
    });

    const pagePath = computed<string>(() => {
      return getWebsiteOrigin() + "/collections/";
    });

    const headerButtonList = computed<
      Array<{ text: string; href: string; target?: string }>
    >(() =>
      [
        collection.value
          ? {
              text: "Посмотреть на сайте",
              href:
                getWebsiteOrigin() +
                "/collections/" +
                collection.value.urlAlias,
              target: "_blank",
            }
          : null,
      ].filter(isNotNullish)
    );

    function handleSearchEngineOptimization({
      title,
      description,
      image,
    }: SeoChangeEvent) {
      values.value.pageTitle = title;
      values.value.pageDescription = description;
      values.value.openGraphImage = image;
    }

    return {
      backButtonUrl: getCollectionListUrl(),
      values,
      errors,
      isSubmitting,
      submitForm,
      isContentLoading,
      collection,
      isCreation,
      pageTitle,
      tabList,
      selectedTabId,
      pagePath,
      brandListOption,
      headerButtonList,

      handleSearchEngineOptimization,
    };
  },
});
